import type {UseMessagesReturn} from '@/components/ChatSection/hooks/useMessages'
import SkipLink from '@/components/SkipLink'
import type {Question} from '@/lib/assistantMessage/splitQuestionFromMessage'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'

import style from './InnoSearchQuestion.module.scss'

interface Props {
  question: Question
  setQ: Dispatch<SetStateAction<string>>
  isDisabled: boolean
  submitMessage: UseMessagesReturn['submitMessage']
}

export default memo<Props>(function InnoSearchQuestion({
  question,
  setQ,
  isDisabled: isDiabled,
  submitMessage,
}) {
  // Note: Thinking about lifting `selected` to Chat component
  const [selected, setSelected] = useState<string[]>([])

  // TODO: fix behavior when user selected in middle of a generating response
  const select = useCallback(
    (answer: string) => {
      setSelected(selected => {
        if (selected.includes(answer)) {
          return selected.filter(s => s !== answer)
        }
        const newSelected = selected.slice()
        newSelected.push(answer)

        let newQ = ''
        if (selected.length > 1) {
          newQ += `${selected.slice(0, -1).join(', ')} and ${selected[selected.length - 1]}`
        } else {
          newQ = selected[0] ?? ''
        }
        setQ(newQ)

        return newSelected
      })
    },
    [setQ],
  )

  return (
    <form
      aria-label='Your answer'
      onSubmit={e => {
        e.preventDefault()
      }}
    >
      <VisuallyHidden>
        To make it easier for you to answer our question, we prepare some viable options for you to
        select. You can select multiple option and it will automatically populate your message form
        for you. Then you can choose to edit your message to add more details or send it right away.
      </VisuallyHidden>
      <ul className={style.Question}>
        {Array.isArray(question.answers) &&
          question.answers.map(answer => (
            <li key={answer}>
              <button
                className={clsx(style.Answer, selected.includes(answer) && style.IsSelected)}
                onClick={() => {
                  select(answer)
                }}
                disabled={isDiabled}
                role='checkbox'
                aria-checked={selected.includes(answer)}
              >
                {answer}
              </button>
            </li>
          ))}
      </ul>
      <SkipLink to='#message-form' title='Skip to message form' />
      <VisuallyHidden as='button' onClick={submitMessage}>
        Send message
      </VisuallyHidden>
    </form>
  )
})
